import React, { useState, useEffect } from 'react';
import { TrackedObject } from '../services/ObjectsService';

export interface ObjectFormProps {
  mode: 'create' | 'edit';
  initialData?: TrackedObject;
  onSubmit: (data: Partial<TrackedObject>) => Promise<void>;
  onCancel: () => void;
  apiError: string | null;
}

interface FormData {
  name: string;
  description: string;
  type: string;
  iconUrl: string;
  idToken: string;
  refreshToken: string;
}

const ObjectForm: React.FC<ObjectFormProps> = ({ mode, initialData, onSubmit, onCancel, apiError }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    type: '',
    iconUrl: '',
    idToken: '',
    refreshToken: '',
  });
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        type: initialData.type || '',
        iconUrl: initialData.iconUrl || '',
        idToken: initialData.idToken || '',
        refreshToken: initialData.refreshToken || '',
      });
    }
  }, [mode, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'name') {
      setNameError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return;
    }

    const data: Partial<TrackedObject> = {
      name: trimmedName,
      description: formData.description.trim() || undefined,
      type: formData.type.trim() || undefined,
      iconUrl: formData.iconUrl.trim() || undefined,
      idToken: formData.idToken.trim() || undefined,
      refreshToken: formData.refreshToken.trim() || undefined,
    };

    setSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="object-form">
      <h3>{mode === 'create' ? 'Create New Object' : 'Edit Object'}</h3>

      {apiError && <div className="message error">{apiError}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="obj-name">Name *</label>
          <input
            id="obj-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter object name"
          />
          {nameError && <div className="validation-error">{nameError}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="obj-description">Description</label>
          <textarea
            id="obj-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter description"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="obj-type">Type</label>
          <input
            id="obj-type"
            type="text"
            name="type"
            value={formData.type}
            onChange={handleChange}
            placeholder="Enter object type"
          />
        </div>

        <div className="form-group">
          <label htmlFor="obj-iconUrl">Icon URL</label>
          <input
            id="obj-iconUrl"
            type="text"
            name="iconUrl"
            value={formData.iconUrl}
            onChange={handleChange}
            placeholder="Enter icon URL"
          />
        </div>

        <div className="form-group">
          <label htmlFor="obj-idToken">ID Token</label>
          <input
            id="obj-idToken"
            type="text"
            name="idToken"
            value={formData.idToken}
            onChange={handleChange}
            placeholder="Enter Sidewalk ID token"
          />
        </div>

        <div className="form-group">
          <label htmlFor="obj-refreshToken">Refresh Token</label>
          <input
            id="obj-refreshToken"
            type="text"
            name="refreshToken"
            value={formData.refreshToken}
            onChange={handleChange}
            placeholder="Enter Sidewalk refresh token"
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ObjectForm;
